// Growem — one-shot intake. Rendered by the App gate when profile.planStatus === 'onboarding'.
// Single vertical scroll of grouped .card sections + sticky submit. Writes via store.saveOnboarding
// -> submit_onboarding RPC -> plan_status='pending' -> App shows <Waiting/>.
// Weight is captured in the chosen unit and CONVERTED to canonical kg before submit, so weight_kg is
// never poisoned by an lb value (data-integrity M1). DOB is age-bounded 13-100 (M2). All inputs are
// programmatically labelled and errors announced (a11y M1-M4).

import { useMemo, useRef, useState } from 'react'
import { useStore } from '../state/store'
import type { OnboardingPayload, Sexo, Objetivo, Nivel, Unidades } from '../types'

const SEXO:   [Sexo, string][]     = [['hombre','Hombre'],['mujer','Mujer'],['otro','Otro']]
// Plain-language labels (no gym jargon): the internal enum values are unchanged, only the copy.
const OBJ:    [Objetivo, string][] = [['hipertrofia','Ganar músculo'],['perder_grasa','Perder grasa'],['recomposicion','Ganar músculo y perder grasa'],['fuerza','Levantar más pesado']]
const NIVEL:  [Nivel, string][]    = [['principiante','Principiante'],['intermedio','Intermedio'],['avanzado','Avanzado']]
// 0=Lunes..6=Domingo. Short glyph for the chip, full name for the accessible label (a11y M2).
const DIAS:   [number, string, string][] = [
  [0,'L','Lunes'],[1,'M','Martes'],[2,'X','Miércoles'],[3,'J','Jueves'],
  [4,'V','Viernes'],[5,'S','Sábado'],[6,'D','Domingo'],
]
// v2 (2026-07-31): duracion (system decides), equipo (future "lugares" feature), lesiones-chips (now
// free text) and objetivo nutricional (redundant with objetivo, we derive it) were REMOVED from the
// form. Their columns stay; the RPC accepts them as optional. See the plain-language rule in memory.

const LB_PER_KG = 2.2046226218
const round1 = (v: number) => Math.round(v * 10) / 10
// ISO date (YYYY-MM-DD) exactly `years` before today — the DOB min/max bounds.
function isoYearsAgo(years: number): string {
  const d = new Date(); d.setFullYear(d.getFullYear() - years)
  return d.toISOString().slice(0, 10)
}
const DOB_MAX = isoYearsAgo(13)   // must be at least 13 years old
const DOB_MIN = isoYearsAgo(100)  // and at most 100

function Opt({ on, onClick, children, label }:
  { on: boolean; onClick: () => void; children: React.ReactNode; label?: string }) {
  return (
    <button type="button" className={on ? 'opt sel' : 'opt'} aria-pressed={on} aria-label={label} onClick={onClick}>
      {children}
    </button>
  )
}

export default function Onboarding() {
  const { saveOnboarding, signOut } = useStore()

  const [nombre, setNombre] = useState('')
  const [sexo, setSexo] = useState<Sexo | null>(null)
  const [fecha, setFecha] = useState('')
  const [estatura, setEstatura] = useState('')
  const [peso, setPeso] = useState('')
  const [objetivo, setObjetivo] = useState<Objetivo | null>(null)
  const [nivel, setNivel] = useState<Nivel | null>(null)
  const [pesoMeta, setPesoMeta] = useState('')
  const [dias, setDias] = useState<number[]>([])
  const [lesNota, setLesNota] = useState('')
  const [fuerza, setFuerza] = useState('')
  const [restr, setRestr] = useState('')
  const [unidades, setUnidades] = useState<Unidades>('kg')

  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const topRef = useRef<HTMLDivElement>(null)

  const toggleDay = (i: number) =>
    setDias((p) => (p.includes(i) ? p.filter((d) => d !== i) : [...p, i].sort((a, b) => a - b)))

  // Weight entered in the selected unit; bands + canonical kg depend on it (M1).
  const est = Number(estatura)                                   // always cm
  const pesRaw = Number(peso)                                    // in selected unit
  // lb floor is 67, NOT 66: 66 lb converts to 29.94 -> rounds to 29.9 kg < the server's 30 kg floor,
  // so the client's own advertised minimum would hard-fail the RPC (audit MUST_FIX). 67 lb = 30.4 kg.
  const wMin = unidades === 'lb' ? 67 : 30
  const wMax = unidades === 'lb' ? 550 : 250
  const pesoKg = unidades === 'lb' ? pesRaw / LB_PER_KG : pesRaw
  const metaRaw = Number(pesoMeta)
  const metaKg = unidades === 'lb' ? metaRaw / LB_PER_KG : metaRaw

  const dobOk = useMemo(() => !!fecha && fecha >= DOB_MIN && fecha <= DOB_MAX, [fecha])
  const estOk = est >= 120 && est <= 230
  // Validate on BOTH the displayed band and the canonical kg the server checks (belt and suspenders).
  const pesoOk = pesRaw >= wMin && pesRaw <= wMax && pesoKg >= 30 && pesoKg <= 250
  // Optional goal weight: when typed, it must fit the same canonical band or the RPC rejects the whole
  // submit with a generic error naming no field (audit SHOULD_FIX, 4-lens convergence).
  const metaOk = pesoMeta.trim() === '' || metaRaw <= 0 || (metaKg >= 30 && metaKg <= 250)

  const valid = useMemo(() =>
    nombre.trim() !== '' && !!sexo && dobOk && estOk && pesoOk && metaOk &&
    !!objetivo && !!nivel && dias.length > 0,
  [nombre, sexo, dobOk, estOk, pesoOk, metaOk, objetivo, nivel, dias])

  async function submit() {
    if (busy) return
    if (!valid) {   // enabled-submit path (a11y M3): name what's missing, announce it (M4)
      const miss: string[] = []
      if (!nombre.trim()) miss.push('nombre')
      if (!sexo) miss.push('sexo')
      if (!dobOk) miss.push('fecha de nacimiento (13-100 años)')
      if (!estOk) miss.push('estatura (120-230 cm)')
      if (!pesoOk) miss.push(`peso (${wMin}-${wMax} ${unidades})`)
      if (!metaOk) miss.push(`peso meta (${wMin}-${wMax} ${unidades})`)
      if (!objetivo) miss.push('objetivo')
      if (!nivel) miss.push('nivel')
      if (dias.length === 0) miss.push('días')
      setErr(`Falta: ${miss.join(', ')}.`)
      topRef.current?.scrollIntoView({ behavior: 'smooth' })
      return
    }
    setBusy(true); setErr(null)
    const payload: OnboardingPayload = {
      nombre: nombre.trim(), sexo: sexo!, fechaNacimiento: fecha,
      estaturaCm: est, pesoKg: round1(pesoKg), objetivo: objetivo!, nivel: nivel!,
      ...(pesoMeta && metaRaw > 0 ? { pesoMetaKg: round1(metaKg) } : {}),
      dias,
      // v2: duracion/equipo/nutricion are omitted (system-decided or derived); injuries travel as
      // ONE free-text nota. The RPC treats all of them as optional.
      ...(lesNota.trim() ? { lesionesNota: lesNota.trim() } : {}),
      ...(fuerza.trim() ? { fuerza: fuerza.trim() } : {}),
      ...(restr.trim() ? { restricciones: restr.trim() } : {}),
      unidades,
    }
    const { error } = await saveOnboarding(payload)
    setBusy(false)
    if (error) { setErr(error); topRef.current?.scrollIntoView({ behavior: 'smooth' }) }
  }

  return (
    <div style={page}>
      <div style={scroll}>
        <div ref={topRef} />
        <div className="disp" style={{ fontWeight: 700, fontSize: 30 }}>growem<span style={{ color: 'var(--volt)' }}>.</span></div>
        <div className="h1" style={{ marginTop: 14 }}>Vamos a armar tu plan</div>
        <p className="tip" style={{ marginTop: 8 }}>
          Responde una vez. Con esto diseñamos tu programa a tu medida: tus días, tus metas y tu punto de partida.
        </p>

        {/* Preferencias primero: la unidad gobierna el label + banda del peso */}
        <section className="card" style={sec} role="group" aria-label="Preferencias">
          <div className="kick">preferencias</div>
          <label style={lbl} id="ob-unidades-lbl">unidades</label>
          <div className="optRow" role="group" aria-labelledby="ob-unidades-lbl">
            {(['kg','lb'] as Unidades[]).map((v) => <Opt key={v} on={unidades === v} onClick={() => setUnidades(v)} label={v === 'kg' ? 'Kilogramos' : 'Libras'}>{v}</Opt>)}
          </div>
        </section>

        {/* Básicos */}
        <section className="card" style={sec} role="group" aria-label="Básicos">
          <div className="kick">básicos</div>
          <label style={lbl} htmlFor="ob-nombre">nombre</label>
          <input id="ob-nombre" style={inp} autoComplete="name" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="¿Cómo te llamas?" />
          <label style={lbl} id="ob-sexo-lbl">sexo</label>
          <div className="optRow" role="group" aria-labelledby="ob-sexo-lbl">{SEXO.map(([v, l]) => <Opt key={v} on={sexo === v} onClick={() => setSexo(v)}>{l}</Opt>)}</div>
          <label style={lbl} htmlFor="ob-fecha">fecha de nacimiento</label>
          <input id="ob-fecha" style={inp} type="date" min={DOB_MIN} max={DOB_MAX} value={fecha} onChange={(e) => setFecha(e.target.value)} />
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl} htmlFor="ob-est">estatura (cm)</label>
              <input id="ob-est" style={inp} type="number" inputMode="numeric" value={estatura} onChange={(e) => setEstatura(e.target.value)} placeholder="175" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl} htmlFor="ob-peso">peso ({unidades})</label>
              <input id="ob-peso" style={inp} type="number" inputMode="decimal" value={peso} onChange={(e) => setPeso(e.target.value)} placeholder={unidades === 'lb' ? '160' : '72'} />
            </div>
          </div>
        </section>

        {/* Objetivo */}
        <section className="card" style={sec} role="group" aria-label="Objetivo">
          <div className="kick">objetivo</div>
          <label style={lbl} id="ob-obj-lbl">objetivo principal</label>
          <div className="optRow" role="group" aria-labelledby="ob-obj-lbl">{OBJ.map(([v, l]) => <Opt key={v} on={objetivo === v} onClick={() => setObjetivo(v)}>{l}</Opt>)}</div>
          <label style={lbl} id="ob-niv-lbl">nivel</label>
          <div className="optRow" role="group" aria-labelledby="ob-niv-lbl">{NIVEL.map(([v, l]) => <Opt key={v} on={nivel === v} onClick={() => setNivel(v)}>{l}</Opt>)}</div>
          <label style={lbl} htmlFor="ob-meta">peso meta (opcional, {unidades})</label>
          <input id="ob-meta" style={inp} type="number" inputMode="decimal" value={pesoMeta} onChange={(e) => setPesoMeta(e.target.value)} placeholder={unidades === 'lb' ? '150' : '68'} />
        </section>

        {/* Entrenamiento */}
        <section className="card" style={sec} role="group" aria-label="Entrenamiento">
          <div className="kick">entrenamiento</div>
          <label style={lbl} id="ob-dias-lbl">días que normalmente puedes entrenar</label>
          <div className="optRow" role="group" aria-labelledby="ob-dias-lbl">{DIAS.map(([i, short, full]) => <Opt key={i} on={dias.includes(i)} onClick={() => toggleDay(i)} label={full}>{short}</Opt>)}</div>
          <span className="muted" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>{dias.length} día(s) por semana</span>
          <p className="tip" style={{ marginTop: 8, marginBottom: 0 }}>
            Es tu semana normal, no una camisa de fuerza: si una semana cambia (descansas otro día, recuperas el fin de semana), no pasa nada. Se puede ajustar después.
          </p>
        </section>

        {/* Salud - free text beats a body-part checklist: specificity is the signal (v2). */}
        <section className="card" style={sec} role="group" aria-label="Salud">
          <div className="kick">salud</div>
          <label style={lbl} htmlFor="ob-lesnota">lesiones o limitaciones (opcional)</label>
          <p className="tip" style={{ margin: '0 0 4px' }} id="ob-lesnota-tip">
            Cuéntanos con detalle: qué te molesta, en qué movimiento exacto, desde cuándo y qué lo empeora o alivia. Mientras más específico, mejor cuidamos tu plan.
          </p>
          <textarea id="ob-lesnota" style={ta} rows={4} maxLength={2000} value={lesNota} onChange={(e) => setLesNota(e.target.value)}
            aria-describedby="ob-lesnota-tip"
            placeholder="Ej: el hombro izquierdo solo me molesta al hacer elevaciones laterales con mancuerna, con máquina no. Me operaron la rodilla derecha en 2023 y evito impacto." />
        </section>

        {/* Fuerza actual */}
        <section className="card" style={sec} role="group" aria-label="Fuerza actual">
          <div className="kick">fuerza actual (opcional)</div>
          <label style={lbl} htmlFor="ob-fuerza">tus marcas</label>
          <textarea id="ob-fuerza" style={ta} rows={3} maxLength={2000} value={fuerza} onChange={(e) => setFuerza(e.target.value)} placeholder="Ej: press banca 60 kg, sentadilla 90 kg, dominadas 8…" />
        </section>

        {/* Nutrición - the "objetivo nutricional" question was REMOVED (redundant with the main goal
            above; we derive how to eat from the goal + body data when building the plan). */}
        <section className="card" style={sec} role="group" aria-label="Nutrición">
          <div className="kick">nutrición</div>
          <label style={lbl} htmlFor="ob-restr">restricciones o alergias (opcional)</label>
          <textarea id="ob-restr" style={ta} rows={2} maxLength={2000} value={restr} onChange={(e) => setRestr(e.target.value)} placeholder="Ej: intolerante a la lactosa, no como cerdo, alergia al maní…" />
        </section>

        <button type="button" className="link" onClick={() => void signOut()}
                style={{ display: 'block', margin: '18px auto 0', background: 'none', fontSize: 13, minHeight: 44 }}>
          Cerrar sesión
        </button>
      </div>

      <div style={footer}>
        {err && <div className="warn" role="alert" style={{ marginBottom: 10 }}>{err}</div>}
        <button className="btn" onClick={submit} disabled={busy} aria-disabled={!valid || busy}
                style={{ opacity: !valid || busy ? 0.55 : 1 }}>
          {busy ? 'Enviando…' : 'Enviar y armar mi plan'}
        </button>
      </div>
    </div>
  )
}

const page:   React.CSSProperties = { minHeight: '100dvh', maxWidth: 'var(--app-max)', margin: '0 auto', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }
const scroll: React.CSSProperties = { flex: 1, minHeight: 0, overflowY: 'auto', padding: '24px 18px 16px' }
const footer: React.CSSProperties = { padding: '12px 18px calc(14px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--line-08)', background: 'var(--bg)' }
const sec:    React.CSSProperties = { marginTop: 12, display: 'flex', flexDirection: 'column' }
const lbl:    React.CSSProperties = { fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--ink-3)', fontWeight: 600, marginTop: 14, marginBottom: 8 }
// NOTE: no `outline:'none'` here — lets the app.css :focus-visible ring show (a11y S1).
const inp:    React.CSSProperties = { background: 'var(--surface-2)', border: '1px solid var(--line-12)', borderRadius: 'var(--r-sm)', padding: '12px 14px', fontSize: 15, width: '100%' }
const ta:     React.CSSProperties = { ...inp, marginTop: 10, resize: 'none' }
